import { CSSProperties, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { StrikeClickInfo } from "./payoffChart/payoffChart.typings";
import { optionsTradesDataType } from "../../pages/typings/options.typings";
import { Security } from "../../proto/generated/security";
import { TransactionType } from "../../proto/generated/order";
import { createStringDateFromStamp } from "../../util/tradePage";
import { Box, ListItemIcon, ListItemText, MenuItem, MenuList, Popper, useTheme } from "@mui/material";
import ThinArrowUpIcon from "../../assets/icons/ThinArrowUpIcon";
import { SQUARE_WIDTH } from "./payoffChart/plusIconDrawing/plusIcon.paneRenderer";
import { getStrikePopoverSurfaceSx } from "../../pages/home/optionChainViewTabs/components/strikePopoverStyles";

export type AddLegMenuProps = {
    clickInfo: StrikeClickInfo,
    optionsTradesData: optionsTradesDataType,
    anchorContainer: HTMLElement,
    onAddLeg: (transactionType: TransactionType, security: Security) => void
    onClose: () => void
}

const AddLegMenu: React.FC<AddLegMenuProps> = ({ clickInfo, optionsTradesData, anchorContainer, onAddLeg, onClose }) => {
    const theme = useTheme()
    const menuSurfaceSx = useMemo(() => getStrikePopoverSurfaceSx(theme), [theme])
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const paperRef = useRef<HTMLDivElement>(null);

    const anchorStyle: CSSProperties = useMemo(() => ({
        position: 'absolute',
        left: clickInfo.x,
        top: clickInfo.y,
        width: SQUARE_WIDTH,
        height: SQUARE_WIDTH,
        pointerEvents: 'none'
    }), [clickInfo])

    const handleAddLeg = useCallback((transactionType: TransactionType, security: Security) => {
        onAddLeg(transactionType, security);
        onClose()
    }, [onAddLeg, onClose])

    useEffect(() => {
        if (!anchorEl) return

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node
            if (anchorEl.contains(target)) return
            if (paperRef.current?.contains(target)) return
            onClose()
        }

        const attachTimer = window.setTimeout(() => {
            document.addEventListener('mousedown', handlePointerDown, true)
        }, 0)

        return () => {
            window.clearTimeout(attachTimer)
            document.removeEventListener('mousedown', handlePointerDown, true)
        }
    }, [anchorEl, onClose])

    const strikeMenuItems = useMemo(() => {
        const menuItems: JSX.Element[] = [];

        const optionTradeData = optionsTradesData.trades.find(t => t.strikeCol.strike === clickInfo.strike);

        if (!optionTradeData) return menuItems

        const callOption = optionTradeData.callOption;
        const putOption = optionTradeData.putOption;

        const expiryText = createStringDateFromStamp((callOption || putOption)?.security?.expiry?.expiry ?? 0)
        const strikeText = `${expiryText} ${optionTradeData.strikeCol.strike / ((callOption || putOption)?.security?.priceMultiplier ?? 100)}`


        menuItems.push(
            <MenuItem key={`buy-${callOption?.security.code}`} onClick={() => handleAddLeg(TransactionType.BUY, callOption?.security)}>
                <ListItemIcon>
                    <ThinArrowUpIcon sx={{fontSize: '1.5rem !important'}}/>
                </ListItemIcon>
                <ListItemText>
                    Buy {`${strikeText} CE`}
                </ListItemText>
            </MenuItem>)
        menuItems.push(
            <MenuItem key={`sell-${callOption?.security.code}`} onClick={() => handleAddLeg(TransactionType.SELL, callOption?.security)}>
                <ListItemIcon>
                    <ThinArrowUpIcon sx={{fontSize: '1.5rem !important'}}/>
                </ListItemIcon>
                <ListItemText>
                    Sell {`${strikeText} CE`}
                </ListItemText>
            </MenuItem>)
        menuItems.push(
            <MenuItem key={`buy-${putOption?.security.code}`} onClick={() => handleAddLeg(TransactionType.BUY, putOption?.security)}>
                <ListItemIcon>
                    <ThinArrowUpIcon sx={{fontSize: '1.5rem !important'}}/>
                </ListItemIcon>
                <ListItemText>
                    Buy {`${strikeText} PE`}
                </ListItemText>
            </MenuItem>)
        menuItems.push(
            <MenuItem key={`sell-${putOption?.security.code}`} onClick={() => handleAddLeg(TransactionType.SELL, putOption?.security)}>
                <ListItemIcon>
                    <ThinArrowUpIcon sx={{fontSize: '1.5rem !important'}}/>
                </ListItemIcon>
                <ListItemText>
                    Sell {`${strikeText} PE`}
                </ListItemText>
            </MenuItem>)

        return menuItems;
    }, [clickInfo, optionsTradesData, handleAddLeg])

    if (!strikeMenuItems.length) return null

    return createPortal(
        <>
            <div ref={setAnchorEl} style={anchorStyle} />
            {anchorEl ? (
                <Popper
                    open
                    disablePortal
                    anchorEl={anchorEl}
                    placement="top-start"
                    modifiers={[
                        { name: 'offset', options: { offset: [0, 0] } },
                        { name: 'flip', enabled: false }
                    ]}
                    sx={{ zIndex: theme.zIndex.modal, pointerEvents: 'none' }}
                >
                    <Box ref={paperRef} sx={{ ...menuSurfaceSx, pointerEvents: 'auto' }}>
                        <MenuList dense disablePadding>
                            {strikeMenuItems}
                        </MenuList>
                    </Box>
                </Popper>
            ) : null}
        </>,
        anchorContainer
    )
};

export default memo(AddLegMenu)
